import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Alert
} from '@mui/material';
import {
  CalendarToday,
  Phone,
  LocationOn,
  Info
} from '@mui/icons-material';

const TravelDetailsForm = ({ bookingData, onUpdate, trialInfo }) => {
  const handleChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  // Calculate minimum dates
  const today = new Date().toISOString().split('T')[0];
  const minEndDate = bookingData.tripStartDate || today;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* Header */}
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
          Travel Details
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Please provide your trip information
        </Typography>

        {/* Trial Info (if provided) */}
        {trialInfo && (
          <Alert severity="info" sx={{ mb: 4 }} icon={<Info />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Trial: {trialInfo.officialTitle}
            </Typography>
            <Typography variant="body2">
              {trialInfo.facility}, {trialInfo.city}, {trialInfo.state}
            </Typography>
          </Alert>
        )}

        {/* DESTINATION SECTION */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            pb: 1,
            borderBottom: '2px solid #1A5EA8'
          }}>
            <LocationOn sx={{ mr: 1, color: '#1A5EA8' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Destination
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              required
              label="Destination City"
              value={bookingData.destinationCity}
              onChange={(e) => handleChange('destinationCity', e.target.value)}
              placeholder="Boston"
            />

            <TextField
              fullWidth
              required
              label="State"
              value={bookingData.destinationState}
              onChange={(e) => handleChange('destinationState', e.target.value)}
              placeholder="MA"
              inputProps={{ maxLength: 2 }}
              helperText="2-letter state code"
            />

            <TextField
              fullWidth
              required
              label="Trial Site Name"
              value={bookingData.trialSiteName}
              onChange={(e) => handleChange('trialSiteName', e.target.value)}
              placeholder="Boston Medical Center"
            />

            <TextField
              fullWidth
              label="Site Address (Optional)"
              value={bookingData.trialSiteAddress}
              onChange={(e) => handleChange('trialSiteAddress', e.target.value)}
              placeholder="1 Boston Medical Center Pl, Boston, MA 02118"
            />
          </Box>
        </Box>

        {/* TRIP DATES SECTION */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            pb: 1,
            borderBottom: '2px solid #1A5EA8'
          }}>
            <CalendarToday sx={{ mr: 1, color: '#1A5EA8' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Trip Dates
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              required
              type="date"
              label="Trip Start Date"
              value={bookingData.tripStartDate}
              onChange={(e) => handleChange('tripStartDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
            />

            <TextField
              fullWidth
              required
              type="date"
              label="Trip End Date"
              value={bookingData.tripEndDate}
              onChange={(e) => handleChange('tripEndDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minEndDate }}
            />
          </Box>
        </Box>

        {/* CONTACT INFORMATION SECTION */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            pb: 1,
            borderBottom: '2px solid #1A5EA8'
          }}>
            <Phone sx={{ mr: 1, color: '#1A5EA8' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Contact Information
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              required
              label="Your Phone Number"
              value={bookingData.patientPhone}
              onChange={(e) => handleChange('patientPhone', e.target.value)}
              placeholder="555-123-4567"
              type="tel"
            />

            <TextField
              fullWidth
              label="Emergency Contact Name (Optional)"
              value={bookingData.emergencyContact}
              onChange={(e) => handleChange('emergencyContact', e.target.value)}
              placeholder="Jane Doe"
            />

            <TextField
              fullWidth
              label="Emergency Contact Phone (Optional)"
              value={bookingData.emergencyPhone}
              onChange={(e) => handleChange('emergencyPhone', e.target.value)}
              placeholder="555-987-6543"
              type="tel"
            />
          </Box>
        </Box>

        {/* SPECIAL REQUIREMENTS SECTION */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            pb: 1,
            borderBottom: '2px solid #1A5EA8'
          }}>
            <Info sx={{ mr: 1, color: '#1A5EA8' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Special Requirements
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Special Requirements (Optional)"
            value={bookingData.specialRequirements}
            onChange={(e) => handleChange('specialRequirements', e.target.value)}
            placeholder="E.g., wheelchair accessible, dietary restrictions, mobility assistance needed"
            helperText="Let us know if you need any special accommodations"
          />
        </Box>

        {/* Required Fields Notice */}
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="body2">
            Fields marked with <strong>*</strong> are required
          </Typography>
        </Alert>

        {/* Hidden Trial Fields */}
        {trialInfo && (
          <>
            <input type="hidden" value={bookingData.trialId} />
            <input type="hidden" value={bookingData.trialName} />
          </>
        )}
      </Paper>
    </Container>
  );
};

export default TravelDetailsForm;